import { clone, ownerKey } from "./types";
import type { Kind, Owner, Snapshot, Work } from "./types";

const LOCK = "pm-offline-locked";
let volatileLock = "";
export function lockVersion() {
    try { return localStorage.getItem(LOCK) || volatileLock; } catch { return volatileLock; }
}
const listeners = new Set<() => void>();
const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel("pm-offline-work");
const emit = () => listeners.forEach(listener => listener());
channel?.addEventListener("message", emit);
export function onWorkChanged(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; }
function announce() { emit(); channel?.postMessage("changed"); }
let opening: Promise<IDBDatabase> | undefined;
function database() {
    return opening ??= new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open("promaster-offline-work", 1);
        request.onupgradeneeded = () => {
            request.result.createObjectStore("work", { keyPath: "id" });
            request.result.createObjectStore("meta");
        };
        request.onsuccess = () => { request.result.onversionchange = () => { request.result.close(); opening = undefined; }; resolve(request.result); };
        request.onerror = () => { opening = undefined; reject(request.error); };
        request.onblocked = () => { opening = undefined; reject(new Error("Offline storage is unavailable")); };
    });
}
async function transact<T>(store: string, write: boolean, action: (store: IDBObjectStore, finish: (value: T) => void) => void, notify = true): Promise<T> {
    const db = await database();
    return new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, write ? "readwrite" : "readonly");
        let value: T;
        try { action(tx.objectStore(store), result => { value = result; }); } catch (error) { tx.abort(); reject(error); return; }
        tx.oncomplete = () => { if (write && notify) announce(); resolve(value); };
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error || new Error("Local save failed"));
    });
}
export const activeOwner = () => lockVersion() ? Promise.resolve(undefined) : transact<Owner | undefined>("meta", false, (store, done) => { const r = store.get("owner"); r.onsuccess = () => done(r.result); });
/** Request-start tokens stop a slow response in another tab from restoring an older account. */
export const beginIdentityCheck = () => transact<string>("meta", true, (store, done) => {
    const token = crypto.randomUUID(); store.put(token, "identity_check"); done(token);
}, false);
export const identityCheckIsCurrent = (token: string) => transact<boolean>("meta", false, (store, done) => {
    const request = store.get("identity_check"); request.onsuccess = () => done(request.result === token);
});
export async function setOwner(owner: Owner, generation = lockVersion(), check?: string) {
    if (generation !== lockVersion()) return false;
    const saved = await transact<boolean>("meta", true, (store, done) => {
        const request = store.get("identity_check");
        request.onsuccess = () => {
            if (generation !== lockVersion() || check && request.result !== check) { done(false); return; }
            store.put(owner, "owner"); done(true);
        };
    }, false);
    if (!saved || generation !== lockVersion()) return false;
    volatileLock = "";
    try { localStorage.removeItem(LOCK); } catch { /* IndexedDB remains authoritative. */ }
    announce(); return true;
}
export function lockOfflineWork() {
    volatileLock = crypto.randomUUID();
    try { localStorage.setItem(LOCK, volatileLock); } catch { /* Also clear the persisted identity below. */ }
    announce();
    return transact<void>("meta", true, store => { store.delete("owner"); store.put(crypto.randomUUID(), "identity_check"); });
}
export const getWork = (id: string) => transact<Work | undefined>("work", false, (store, done) => { const r = store.get(id); r.onsuccess = () => done(r.result); });
export const allWork = (owner: Owner) => transact<Work[]>("work", false, (store, done) => {
    const r = store.getAll(); r.onsuccess = () => done((r.result as Work[]).filter(work => work.ownerKey === ownerKey(owner)).sort((a, b) => b.savedAt - a.savedAt));
});
export const removeWork = (id: string) => transact<void>("work", true, store => { store.delete(id); });
/** Patches run against the current persisted revision, including when another tab edited it. */
export const changeWork = (id: string, change: (work: Work) => void) => transact<Work>("work", true, (store, done) => {
    const request = store.get(id);
    request.onsuccess = () => {
        const work: Work | undefined = request.result;
        if (!work) { store.transaction.abort(); return; }
        try { change(work); store.put(work); done(work); } catch { store.transaction.abort(); }
    };
});
export async function prepare(snapshot: Snapshot, copy = false,
                              expected?: { id?: string; revision?: number; version?: string | null }) {
    const owner = snapshot.owner;
    const active = await activeOwner();
    if (!active || ownerKey(active) !== ownerKey(owner) || active.company_id !== owner.company_id
        || active.workspace !== owner.workspace) throw new Error("Sign in before preparing offline work");
    const result = await transact<Work>("work", true, (store, done) => {
        const request = store.getAll();
        request.onsuccess = () => {
            const found = !copy && snapshot.document_id ? (request.result as Work[]).find(work => work.ownerKey === ownerKey(owner)
                && work.base.kind === snapshot.kind && work.base.document_id === snapshot.document_id
                && work.base.owner.company_id === owner.company_id
                && work.base.owner.workspace === owner.workspace) : undefined;
            // A slow snapshot must not roll a document back after another tab
            // has edited it, synchronized it, or prepared it in the meantime.
            if (!copy && snapshot.document_id && expected) {
                if (!found && expected.id) { store.transaction.abort(); return; }
                if (found && (!expected.id || found.id !== expected.id || found.revision !== expected.revision
                    || found.base.version !== expected.version)) { done(found); return; }
            }
            if (found?.dirty || found?.operation) { done(found); return; }
            if (found) {
                const updated = { ...found, base: clone(snapshot), draft: clone(snapshot.draft), savedAt: Date.now(), status: undefined, error: undefined, conflict: undefined };
                store.put(updated); done(updated); return;
            }
            const base = clone(snapshot);
            if (copy) { base.document_id = null; base.version = null; base.state = "draft"; base.editable = true; base.draft = { header: { ...base.draft.header, name: "" }, tables: Object.fromEntries(Object.keys(base.tables).map(key => [key, []])) }; }
            const work: Work = { id: crypto.randomUUID(), ownerKey: ownerKey(owner), base,
                draft: clone(base.draft), revision: 0, savedAt: Date.now(), dirty: !base.document_id, ready: false };
            store.put(work); done(work);
        };
    });
    void navigator.storage?.persist?.().catch(() => false);
    return result;
}

export async function editWork(id: string, edit: (work: Work) => void, ready = true) {
    return changeWork(id, work => {
        edit(work); work.revision++; work.dirty = true; work.ready = ready;
        work.savedAt = Date.now(); work.error = undefined;
        if (!work.conflict) work.status = undefined;
    });
}

/**
 * Apply a regular form edit and allocate its local IDs in the same IndexedDB
 * transaction. A negative ID is never sent to Odoo; the existing row keys and
 * synchronization receipts remain the authoritative wire format.
 */
export async function changeScopedWork(id: string, change?: (work: Work) => void, ready = true): Promise<Work> {
    const db = await database();
    const generation = lockVersion();
    if (generation) throw new Error("Offline workspace is locked");
    return new Promise<Work>((resolve, reject) => {
        const tx = db.transaction(["meta", "work"], "readwrite");
        const meta = tx.objectStore("meta");
        const records = tx.objectStore("work");
        let failure: Error | undefined;
        let result: Work | undefined;
        let changed = false;
        const abort = (message: string) => { failure = new Error(message); tx.abort(); };
        const identity = meta.get("owner");
        identity.onsuccess = () => {
            const owner = identity.result as Owner | undefined;
            if (!owner || generation !== lockVersion()) return abort("Offline workspace is locked");
            const request = records.get(id);
            request.onsuccess = () => {
                const work = request.result as Work | undefined;
                if (!work || work.ownerKey !== ownerKey(owner)
                    || work.base.owner.company_id !== owner.company_id
                    || work.base.owner.workspace !== owner.workspace) {
                    return abort("Offline work belongs to another workspace");
                }
                const sequence = meta.get("local_id_sequence");
                sequence.onsuccess = () => {
                    if (generation !== lockVersion()) return abort("Offline workspace is locked");
                    let next = Number(sequence.result || 0);
                    if (!Number.isSafeInteger(next) || next < 0) return abort("Invalid local ID sequence");
                    const allocate = () => {
                        if (next >= Number.MAX_SAFE_INTEGER - 1) throw new Error("Local ID sequence exhausted");
                        return -(++next);
                    };
                    try {
                        if (!work.base.document_id && !work.localId) { work.localId = allocate(); changed = true; }
                        const assignRows = () => {
                            for (const [table, rows] of Object.entries(work.draft.tables)) {
                                for (const row of rows) {
                                    if (row.id !== null) continue;
                                    work.localRows ??= {};
                                    work.localRows[table] ??= {};
                                    if (!work.localRows[table]![row.key]) {
                                        work.localRows[table]![row.key] = allocate(); changed = true;
                                    }
                                }
                            }
                        };
                        assignRows();
                        if (change) {
                            change(work);
                            work.revision++; work.dirty = true; work.ready = ready;
                            work.savedAt = Date.now(); work.error = undefined;
                            if (!work.conflict) work.status = undefined;
                            changed = true;
                        }
                        assignRows();
                        if (changed) { meta.put(next, "local_id_sequence"); records.put(work); }
                        result = work;
                    } catch (error) {
                        failure = error instanceof Error ? error : new Error("Local save failed");
                        tx.abort();
                    }
                };
            };
        };
        tx.oncomplete = () => {
            if (changed) announce();
            if (result) resolve(result); else reject(new Error("Local save failed"));
        };
        tx.onerror = () => reject(failure || tx.error || new Error("Local save failed"));
        tx.onabort = () => reject(failure || tx.error || new Error("Local save failed"));
    });
}

/** Only shell data for the last verified identity is available without a network. */
export async function shellCache<T>(part: "navigation" | "context", write?: { userId: number; companyId: number; value: T }): Promise<T | undefined> {
    const db = await database();
    const generation = lockVersion();
    if (generation) return undefined;
    return new Promise<T | undefined>((resolve, reject) => {
        const tx = db.transaction("meta", write ? "readwrite" : "readonly");
        const store = tx.objectStore("meta");
        let result: T | undefined;
        const identity = store.get("owner");
        identity.onsuccess = () => {
            const owner = identity.result as Owner | undefined;
            if (!owner || generation !== lockVersion() || (write && (write.userId !== owner.user_id
                || write.companyId !== owner.company_id))) return;
            const key = `shell:${owner.instance}:${owner.user_id}:${owner.company_id}:${owner.workspace}:${part}`;
            if (write) store.put(write.value, key);
            else {
                const request = store.get(key);
                request.onsuccess = () => { if (generation === lockVersion()) result = request.result as T | undefined; };
            }
        };
        tx.oncomplete = () => resolve(generation === lockVersion() ? result : undefined);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error || new Error("Local storage unavailable"));
    });
}

/** Project templates contain the server's creation defaults and permissions. */
export async function projectTemplates(kind: Kind, snapshot?: Snapshot): Promise<Snapshot[]> {
    const db = await database();
    const generation = lockVersion();
    if (generation) return [];
    return new Promise<Snapshot[]>((resolve, reject) => {
        const tx = db.transaction("meta", snapshot ? "readwrite" : "readonly");
        const store = tx.objectStore("meta");
        let result: Snapshot[] = [];
        let changed = false;
        const identity = store.get("owner");
        identity.onsuccess = () => {
            const owner = identity.result as Owner | undefined;
            if (!owner || generation !== lockVersion()) return;
            if (snapshot && (snapshot.document_id || snapshot.kind !== kind || ownerKey(snapshot.owner) !== ownerKey(owner)
                || snapshot.owner.company_id !== owner.company_id || snapshot.owner.workspace !== owner.workspace)) return;
            const key = `templates:${owner.instance}:${owner.user_id}:${owner.company_id}:${owner.workspace}:${kind}`;
            const request = store.get(key);
            request.onsuccess = () => {
                if (generation !== lockVersion()) return;
                result = Array.isArray(request.result) ? request.result as Snapshot[] : [];
                if (snapshot) {
                    result = [...result.filter(item => item.project.id !== snapshot.project.id), clone(snapshot)];
                    store.put(result, key); changed = true;
                }
            };
        };
        tx.oncomplete = () => { if (changed) announce(); resolve(generation === lockVersion() ? result : []); };
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error || new Error("Local storage unavailable"));
    });
}
